import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Space,
  Card,
  Row,
  Col,
  Input,
  Modal,
  Form,
  Typography,
  Popconfirm,
  Upload,
  message,
  Tooltip,
  Descriptions,
  Spin,
  Select,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  KeyOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  SwapOutlined,
  CheckCircleFilled,
  WarningOutlined,
} from "@ant-design/icons";
import {
  getMerchants,
  createMerchant,
  updateMerchant,
  deleteMerchant,
  bulkCreateMerchants,
  getMerchantLoginDetails,
  createAssistant,
  deleteAssistant,
  getAssistantLoginDetails,
  bulkReplaceMerchant,
} from "./services/merchantApi";
import "./styles.css";

const { Title, Text } = Typography;
const { Search } = Input;

const Merchant = () => {
  const [merchants, setMerchants] = useState([]);
  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Create/Edit merchant modal
  const [merchantModalOpen, setMerchantModalOpen] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [merchantForm] = Form.useForm();
  const [merchantModalLoading, setMerchantModalLoading] = useState(false);

  // Assistant modal
  const [assistantModalOpen, setAssistantModalOpen] = useState(false);
  const [assistantMerchant, setAssistantMerchant] = useState(null);
  const [assistantForm] = Form.useForm();
  const [assistantModalLoading, setAssistantModalLoading] = useState(false);

  // Login details modal
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginDetails, setLoginDetails] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginTitle, setLoginTitle] = useState("");

  // CSV upload modal
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvUploading, setCsvUploading] = useState(false);

  // Export login details
  const [exportingLogins, setExportingLogins] = useState(false);

  // Bulk merchant assignment replacement
  const [replaceModalOpen, setReplaceModalOpen]   = useState(false);
  const [replaceFromId,    setReplaceFromId]       = useState(null);
  const [replaceToId,      setReplaceToId]         = useState(null);
  const [replaceLoading,   setReplaceLoading]      = useState(false);
  const [replaceResult,    setReplaceResult]       = useState(null); // { replaced_count, from_merchant, to_merchant }

  // ---- Data fetching ----

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMerchants();
      const keyed = data.map((m, i) => ({ ...m, key: m.id || i }));
      setMerchants(keyed);
      setFilteredMerchants(keyed);
    } catch {
      message.error("Failed to load merchants");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  // ---- Search ----

  useEffect(() => {
    if (!searchText) {
      setFilteredMerchants(merchants);
      return;
    }
    const lower = searchText.toLowerCase();
    setFilteredMerchants(
      merchants.filter(
        (m) =>
          m.name?.toLowerCase().includes(lower) ||
          m.email?.toLowerCase().includes(lower) ||
          m.contact?.toLowerCase().includes(lower) ||
          m.assistant?.name?.toLowerCase().includes(lower)
      )
    );
  }, [searchText, merchants]);

  // ---- Create / Edit merchant ----

  const openCreateModal = () => {
    setEditingMerchant(null);
    merchantForm.resetFields();
    setMerchantModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingMerchant(record);
    merchantForm.setFieldsValue({
      name: record.name,
      email: record.email,
      contact: record.contact,
    });
    setMerchantModalOpen(true);
  };

  const handleMerchantSubmit = async () => {
    try {
      const values = await merchantForm.validateFields();
      setMerchantModalLoading(true);
      if (editingMerchant) {
        await updateMerchant(editingMerchant.id, values);
        message.success("Merchant updated successfully");
      } else {
        await createMerchant(values);
        message.success("Merchant created successfully");
      }
      setMerchantModalOpen(false);
      merchantForm.resetFields();
      fetchMerchants();
    } catch (err) {
      if (err.errorFields) return; // validation error
      message.error("Failed to save merchant");
    } finally {
      setMerchantModalLoading(false);
    }
  };

  // ---- Delete merchant ----

  const handleDeleteMerchant = async (record) => {
    try {
      await deleteMerchant(record.id);
      message.success("Merchant deleted successfully");
      fetchMerchants();
    } catch {
      message.error("Failed to delete merchant");
    }
  };

  // ---- Login details ----

  const showMerchantLogin = async (record) => {
    setLoginTitle(`${record.name} - Login Details`);
    setLoginDetails(null);
    setLoginModalOpen(true);
    setLoginLoading(true);
    try {
      const data = await getMerchantLoginDetails(record.id);
      setLoginDetails(data);
    } catch {
      message.error("Failed to load login details");
      setLoginModalOpen(false);
    } finally {
      setLoginLoading(false);
    }
  };

  const showAssistantLogin = async (merchant) => {
    setLoginTitle(`${merchant.assistant.name} - Login Details`);
    setLoginDetails(null);
    setLoginModalOpen(true);
    setLoginLoading(true);
    try {
      const data = await getAssistantLoginDetails(merchant.id, merchant.assistant.id);
      setLoginDetails(data);
    } catch {
      message.error("Failed to load login details");
      setLoginModalOpen(false);
    } finally {
      setLoginLoading(false);
    }
  };

  // ---- Assistants ----

  const openAssistantModal = (record) => {
    setAssistantMerchant(record);
    assistantForm.resetFields();
    setAssistantModalOpen(true);
  };

  const handleAssistantSubmit = async () => {
    try {
      const values = await assistantForm.validateFields();
      setAssistantModalLoading(true);
      await createAssistant(assistantMerchant.id, values);
      message.success("Assistant created successfully");
      setAssistantModalOpen(false);
      assistantForm.resetFields();
      fetchMerchants();
    } catch (err) {
      if (err.errorFields) return;
      message.error("Failed to create assistant");
    } finally {
      setAssistantModalLoading(false);
    }
  };

  const handleDeleteAssistant = async (merchant) => {
    try {
      await deleteAssistant(merchant.id, merchant.assistant.id);
      message.success("Assistant deleted successfully");
      fetchMerchants();
    } catch {
      message.error("Failed to delete assistant");
    }
  };

  // ---- Export all login details ----

  const handleExportLoginDetails = async () => {
    setExportingLogins(true);
    try {
      const rows = [["Type", "Name", "Email", "Password"]];
      const tasks = merchants.flatMap((m) => {
        const tasks = [
          getMerchantLoginDetails(m.id).then((d) => ({
            type: "Merchant",
            name: m.name,
            email: d.email,
            password: d.password,
          })),
        ];
        if (m.assistant) {
          tasks.push(
            getAssistantLoginDetails(m.id, m.assistant.id).then((d) => ({
              type: "Assistant",
              name: m.assistant.name,
              email: d.email,
              password: d.password,
            }))
          );
        }
        return tasks;
      });
      const results = await Promise.allSettled(tasks);
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          const { type, name, email, password } = r.value;
          rows.push([type, name || "", email || "", password || ""]);
        }
      });
      const csvContent = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "merchant-login-details.csv";
      a.click();
      URL.revokeObjectURL(url);
      message.success("Login details exported successfully");
    } catch {
      message.error("Failed to export login details");
    } finally {
      setExportingLogins(false);
    }
  };

  // ---- CSV upload ----

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) return [];

    const results = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (!values[0]) continue; // skip empty rows

      const merchant = {
        name: values[0]?.trim() || "",
        email: values[1]?.trim() || "",
        contact: values[2]?.trim() || "",
      };

      // If assistant columns exist and have data
      if (values[3]?.trim()) {
        merchant.assistant = {
          name: values[3]?.trim() || "",
          email: values[4]?.trim() || "",
          contact: values[5]?.trim() || "",
        };
      }

      if (values[6]?.trim()) {
        merchant.role = values[6].trim();
      }

      results.push(merchant);
    }
    return results;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (parsed.length === 0) {
        message.error("No valid data found in CSV");
        return;
      }
      setCsvData(parsed);
      setCsvModalOpen(true);
    };
    reader.readAsText(file);
    return false; // prevent default upload
  };

  const handleBulkUpload = async () => {
    setCsvUploading(true);
    try {
      await bulkCreateMerchants(csvData);
      message.success(`${csvData.length} merchants uploaded successfully`);
      setCsvModalOpen(false);
      setCsvData([]);
      fetchMerchants();
    } catch {
      message.error("Failed to upload merchants");
    } finally {
      setCsvUploading(false);
    }
  };

  // ---- Bulk merchant replacement ----

  const openReplaceModal = () => {
    setReplaceFromId(null);
    setReplaceToId(null);
    setReplaceResult(null);
    setReplaceModalOpen(true);
  };

  const handleBulkReplace = async () => {
    if (!replaceFromId || !replaceToId) {
      message.warning("Please select both merchants before confirming.");
      return;
    }
    setReplaceLoading(true);
    try {
      const result = await bulkReplaceMerchant(replaceFromId, replaceToId);
      setReplaceResult(result);
      fetchMerchants(); // refresh table so merchant counts stay consistent
    } catch {
      message.error("Bulk replacement failed. Please try again.");
    } finally {
      setReplaceLoading(false);
    }
  };

  const handleCloseReplaceModal = () => {
    setReplaceModalOpen(false);
    setReplaceFromId(null);
    setReplaceToId(null);
    setReplaceResult(null);
  };

  // Helpers for the replace modal
  const fromMerchant = merchants.find((m) => m.id === replaceFromId);
  const toMerchant   = merchants.find((m) => m.id === replaceToId);

  // ---- Table columns ----

  const expandedRowRender = (record) => {
    if (!record.assistant) {
      return (
        <div className="assistant-section">
          <Text type="secondary">No assistant assigned.</Text>
          <Button
            type="link"
            icon={<UserAddOutlined />}
            onClick={() => openAssistantModal(record)}
          >
            Add Assistant
          </Button>
        </div>
      );
    }

    return (
      <div className="assistant-section">
        <Descriptions
          title="Assistant Details"
          size="small"
          column={{ xs: 1, sm: 2, md: 3 }}
          bordered
        >
          <Descriptions.Item label="Name">{record.assistant.name}</Descriptions.Item>
          <Descriptions.Item label="Email">{record.assistant.email}</Descriptions.Item>
          <Descriptions.Item label="Contact">{record.assistant.contact}</Descriptions.Item>
        </Descriptions>
        <Space className="assistant-actions">
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => showAssistantLogin(record)}
          >
            Login Details
          </Button>
          <Popconfirm
            title="Delete assistant?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteAssistant(record)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<UserDeleteOutlined />}>
              Remove Assistant
            </Button>
          </Popconfirm>
        </Space>
      </div>
    );
  };

  const columns = [
    {
      title: "Merchant Name",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Contact",
      dataIndex: "contact",
    },
    {
      title: "Assistant",
      dataIndex: ["assistant", "name"],
      render: (text) => text || <Text type="secondary">None</Text>,
    },
    {
      title: "Actions",
      width: 280,
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="View Login Details">
            <Button
              size="small"
              icon={<KeyOutlined />}
              onClick={() => showMerchantLogin(record)}
            >
              Login
            </Button>
          </Tooltip>
          <Tooltip title="Edit Merchant">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          {!record.assistant ? (
            <Tooltip title="Add Assistant">
              <Button
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => openAssistantModal(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="View Assistant">
              <Button size="small" icon={<EyeOutlined />} />
            </Tooltip>
          )}
          <Popconfirm
            title="Delete this merchant?"
            description="This will permanently remove this merchant."
            onConfirm={() => handleDeleteMerchant(record)}
            okText="Delete"
            okType="danger"
          >
            <Tooltip title="Delete Merchant">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ---- CSV preview columns ----

  const csvColumns = [
    { title: "Name", dataIndex: "name" },
    { title: "Email", dataIndex: "email" },
    { title: "Contact", dataIndex: "contact" },
    {
      title: "Assistant",
      dataIndex: ["assistant", "name"],
      render: (text) => text || "-",
    },
    {
      title: "Assistant Email",
      dataIndex: ["assistant", "email"],
      render: (text) => text || "-",
    },
  ];

  return (
    <div className="merchant-container">
      <Card className="merchant-card">
        {/* Header */}
        <Row
          justify="space-between"
          align="middle"
          className="merchant-header"
          gutter={[16, 16]}
          wrap
        >
          <Col>
            <Title level={4} className="merchant-title">
              Merchant Management
            </Title>
          </Col>
          <Col>
            <Space wrap>
              <Button
                icon={<DownloadOutlined />}
                loading={exportingLogins}
                onClick={handleExportLoginDetails}
              >
                Export Logins
              </Button>
              <Upload
                accept=".csv"
                showUploadList={false}
                beforeUpload={handleCSVFile}
              >
                <Button icon={<UploadOutlined />}>Upload CSV</Button>
              </Upload>
              <Tooltip title="Reassign all contracts from one merchant to another">
                <Button
                  icon={<SwapOutlined />}
                  onClick={openReplaceModal}
                >
                  Bulk Replace
                </Button>
              </Tooltip>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Add Merchant
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Search */}
        <Row className="table-controls" gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search merchants..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="merchant-search"
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: "right" }}>
            <Text type="secondary">
              {filteredMerchants.length} merchant{filteredMerchants.length !== 1 ? "s" : ""}
            </Text>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredMerchants}
          loading={loading}
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
          }}
          scroll={{ x: "max-content" }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} merchants`,
          }}
          className="merchant-table"
        />
      </Card>

      {/* ========== CREATE / EDIT MERCHANT MODAL ========== */}
      <Modal
        title={editingMerchant ? "Edit Merchant" : "Add New Merchant"}
        open={merchantModalOpen}
        onCancel={() => {
          setMerchantModalOpen(false);
          merchantForm.resetFields();
        }}
        onOk={handleMerchantSubmit}
        confirmLoading={merchantModalLoading}
        okText={editingMerchant ? "Update" : "Create"}
        destroyOnClose
      >
        <Form form={merchantForm} layout="vertical" className="merchant-form">
          <Form.Item
            name="name"
            label="Merchant Name"
            rules={[{ required: true, message: "Please enter merchant name" }]}
          >
            <Input placeholder="Enter merchant name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
          <Form.Item
            name="contact"
            label="Contact Number"
            rules={[{ required: true, message: "Please enter contact number" }]}
          >
            <Input placeholder="e.g. 800-257-8220 x249" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ========== ADD ASSISTANT MODAL ========== */}
      <Modal
        title={`Add Assistant for ${assistantMerchant?.name || ""}`}
        open={assistantModalOpen}
        onCancel={() => {
          setAssistantModalOpen(false);
          assistantForm.resetFields();
        }}
        onOk={handleAssistantSubmit}
        confirmLoading={assistantModalLoading}
        okText="Create"
        destroyOnClose
      >
        <Form form={assistantForm} layout="vertical" className="merchant-form">
          <Form.Item
            name="name"
            label="Assistant Name"
            rules={[{ required: true, message: "Please enter assistant name" }]}
          >
            <Input placeholder="Enter assistant name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
          <Form.Item
            name="contact"
            label="Contact Number"
            rules={[{ required: true, message: "Please enter contact number" }]}
          >
            <Input placeholder="e.g. 800-257-8220 x276" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ========== LOGIN DETAILS MODAL ========== */}
      <Modal
        title={loginTitle}
        open={loginModalOpen}
        onCancel={() => setLoginModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setLoginModalOpen(false)}>
            Close
          </Button>,
        ]}
        destroyOnClose
      >
        {loginLoading ? (
          <div className="login-loading">
            <Spin />
          </div>
        ) : loginDetails ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Email">
              {loginDetails.email}
            </Descriptions.Item>
            <Descriptions.Item label="Password">
              {loginDetails.password}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary">No login details available.</Text>
        )}
      </Modal>

      {/* ========== CSV PREVIEW MODAL ========== */}
      <Modal
        title="CSV Upload Preview"
        open={csvModalOpen}
        onCancel={() => {
          setCsvModalOpen(false);
          setCsvData([]);
        }}
        width={800}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setCsvModalOpen(false);
              setCsvData([]);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            icon={<DownloadOutlined />}
            loading={csvUploading}
            onClick={handleBulkUpload}
          >
            Upload {csvData.length} Merchants
          </Button>,
        ]}
        destroyOnClose
      >
        <div className="csv-preview-header">
          <Text>
            Found <strong>{csvData.length}</strong> merchants in the CSV file.
            Review and confirm to upload.
          </Text>
        </div>
        <Table
          columns={csvColumns}
          dataSource={csvData.map((d, i) => ({ ...d, key: i }))}
          size="small"
          scroll={{ x: "max-content", y: 400 }}
          pagination={false}
          className="csv-preview-table"
        />
      </Modal>
      {/* ========== BULK MERCHANT REPLACEMENT MODAL ========== */}
      <Modal
        title={
          <Space>
            <SwapOutlined style={{ color: "#1890ff" }} />
            Bulk Merchant Assignment Replacement
          </Space>
        }
        open={replaceModalOpen}
        onCancel={handleCloseReplaceModal}
        footer={
          replaceResult
            ? [
                <Button key="close" type="primary" onClick={handleCloseReplaceModal}>
                  Done
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={handleCloseReplaceModal}>
                  Cancel
                </Button>,
                <Popconfirm
                  key="confirm"
                  title="Confirm Bulk Replacement"
                  description={
                    fromMerchant && toMerchant
                      ? `Move ALL contracts from "${fromMerchant.name}" to "${toMerchant.name}"? This cannot be undone.`
                      : "Please select both merchants first."
                  }
                  onConfirm={handleBulkReplace}
                  okText="Yes, Replace"
                  okType="danger"
                  cancelText="Go Back"
                  disabled={!replaceFromId || !replaceToId}
                >
                  <Button
                    type="primary"
                    icon={<SwapOutlined />}
                    loading={replaceLoading}
                    disabled={!replaceFromId || !replaceToId}
                    danger
                  >
                    Replace Assignments
                  </Button>
                </Popconfirm>,
              ]
        }
        width={520}
        destroyOnClose
      >
        {replaceResult ? (
          /* ── Success State ── */
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <CheckCircleFilled style={{ fontSize: 48, color: "#52c41a", marginBottom: 16 }} />
            <Typography.Title level={4} style={{ color: "#52c41a", margin: "0 0 8px" }}>
              Replacement Complete
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              Successfully moved{" "}
              <strong>{replaceResult.replaced_count}</strong> contract
              {replaceResult.replaced_count !== 1 ? "s" : ""} from{" "}
              <strong>{replaceResult.from_merchant}</strong> to{" "}
              <strong>{replaceResult.to_merchant}</strong>.
            </Typography.Paragraph>
          </div>
        ) : (
          /* ── Selection Form ── */
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message="All contracts assigned to the selected merchant will be permanently reassigned to the replacement merchant. This action cannot be undone."
            />

            <div>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Transfer FROM (current merchant)
              </Text>
              <Select
                style={{ width: "100%" }}
                placeholder="Select merchant to replace..."
                value={replaceFromId}
                onChange={(val) => {
                  setReplaceFromId(val);
                  // Clear TO if it was the same merchant
                  if (replaceToId === val) setReplaceToId(null);
                }}
                showSearch
                optionFilterProp="label"
                options={merchants.map((m) => ({
                  value: m.id,
                  label: m.name,
                }))}
                size="large"
              />
            </div>

            <div>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Transfer TO (replacement merchant)
              </Text>
              <Select
                style={{ width: "100%" }}
                placeholder="Select replacement merchant..."
                value={replaceToId}
                onChange={setReplaceToId}
                showSearch
                optionFilterProp="label"
                options={merchants
                  .filter((m) => m.id !== replaceFromId)
                  .map((m) => ({
                    value: m.id,
                    label: m.name,
                  }))}
                size="large"
                disabled={!replaceFromId}
              />
            </div>

            {fromMerchant && toMerchant && (
              <div
                style={{
                  background: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: 8,
                  padding: "12px 16px",
                }}
              >
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <SwapOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                  All contracts currently assigned to{" "}
                  <strong>{fromMerchant.name}</strong> will be moved to{" "}
                  <strong>{toMerchant.name}</strong>.
                </Text>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default Merchant;
